import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  isStringNode,
  JsonError,
  JsonSchema,
  Node,
  StringNode,
} from '@sagold/react-json-editor';
import { DateTime } from 'luxon';

import { numberFormatter } from '../../components/table/valueFormatters';
import { getNodeFromJsonPath } from '../plugins/pluginHelper';

export function isNullableString(type: JsonSchema['type']) {
  return (
    type === 'string' ||
    (Array.isArray(type) &&
      type.includes('string') &&
      type.every((t) => t === 'string' || t === 'null'))
  );
}

export function isNullableNumber(type: JsonSchema['type']) {
  return (
    type === 'number' ||
    (Array.isArray(type) &&
      type.includes('number') &&
      type.every((t) => t === 'number' || t === 'null'))
  );
}

export function isNullableInteger(type: JsonSchema['type']) {
  return (
    type === 'integer' ||
    (Array.isArray(type) &&
      type.includes('integer') &&
      type.every((t) => t === 'integer' || t === 'null'))
  );
}

export function useErrorTransformation(errors: JsonError[]) {
  const { t } = useTranslation();
  return useMemo(() => {
    return errors.map((e) => {
      if (
        e.code === 'min-length-one-error' ||
        (e.code === 'type-error' && e.data['received'] === 'null')
      ) {
        return {
          ...e,
          message: t('app.forms.validationErrors.inputIsRequired'),
        };
      }
      if (
        (e.code === 'minimum-error' || e.code === 'maximum-error') &&
        typeof e.data.schema?.minimum === 'number' &&
        typeof e.data.schema?.maximum === 'number' &&
        e.data.schema.minimum === e.data.schema.maximum
      ) {
        const formattedValue = numberFormatter(
          e.data.schema?.minimum,
          e.data.schema?.options ?? {}
        );
        return {
          ...e,
          message: t('app.forms.validationErrors.minMaxNumberError', {
            expected: formattedValue,
          }),
        };
      }
      if (e.code === 'custom-min-date-error') {
        return {
          ...e,
          message:
            t('app.forms.validationErrors.minDateError') + e.compareValue,
        };
      }
      if (e.code === 'custom-max-date-error') {
        return {
          ...e,
          message:
            t('app.forms.validationErrors.maxDateError') + e.compareValue,
        };
      }
      if (e.code === 'custom-should-be-max-date-error') {
        return {
          ...e,
          message: t('app.forms.validationErrors.shouldBeMaxError'),
        };
      }
      return e;
    });
  }, [t, errors]);
}

export const getDateFromString = (
  dateString: string
): DateTime<true> | undefined => {
  const directDate = DateTime.fromISO(dateString);
  if (directDate.isValid) {
    return directDate;
  }
  return undefined;
};

export const getDateFromJsonRef = (
  stringRef: string,
  rootNode: Node,
  dateNode: StringNode
): DateTime<true> | undefined => {
  const referencedNode = getNodeFromJsonPath(
    dateNode.pointer,
    stringRef,
    rootNode
  );
  if (
    referencedNode &&
    isStringNode(referencedNode) &&
    referencedNode.schema.format === 'date' &&
    referencedNode.value
  ) {
    return getDateFromString(referencedNode.value);
  }
  return undefined;
};
